import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import espnowService, { type FieldCommand, type EspNowRouter } from '../../../services/espnowIrrigation';
import { commandsForRelay } from '../../../utils/commandActivity';
import RecentActivity from '../RecentActivity';

/**
 * What this one valve has actually been asked to do.
 *
 * The node drawer could say which relay a node is linked to, which programs
 * water it and whether it is on right now — and nothing at all about the
 * commands that produced any of that. So a valve that is off despite a program
 * covering it looked identical to one nobody had ever driven, and the answer
 * ("the SET_RELAY is still QUEUED — the site has not polled since Tuesday")
 * was on the controller page behind two clicks and a site-wide list.
 *
 * Deliberately scoped to the linked relay rather than showing the site's
 * activity. Somebody opening a node drawer is asking about that node; a list
 * where its one row sits among a whole farm's traffic answers a different
 * question, and the controller page already answers that one.
 */

interface Props {
  controllerId: string;
  routerMac: string;
  relayIndex: number;
  /** The routers of that controller, for naming the valve. */
  routers: EspNowRouter[];
}

type State =
  | { status: 'loading' }
  | { status: 'ready'; commands: FieldCommand[] }
  /**
   * Not folded into the empty state. "Nothing has been sent to this valve" is a
   * statement about the hardware, and printing it over a failed request makes
   * the drawer assert something it does not know.
   */
  | { status: 'failed' };

export default function RelayActivity({ controllerId, routerMac, relayIndex, routers }: Props) {
  const [state, setState] = useState<State>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading' });
    // One controller, one request, and only once a node with a link is open —
    // the farm's other sites have nothing to say about this valve.
    espnowService.getCommandLog(controllerId, 50)
      .then(all => {
        if (cancelled) return;
        // A log that did not arrive as a list is not an empty one. Coercing it
        // would print "nothing has been sent to this valve" over a valve that
        // may well have been driven all morning.
        if (!Array.isArray(all)) { setState({ status: 'failed' }); return; }
        setState({ status: 'ready', commands: commandsForRelay(all, routerMac, relayIndex) });
      })
      .catch(() => { if (!cancelled) setState({ status: 'failed' }); });
    return () => { cancelled = true; };
  }, [controllerId, routerMac, relayIndex]);

  if (state.status === 'loading') {
    return (
      <div className="mt-2 flex items-center gap-1.5 text-gray-500">
        <Loader2 className="w-3 h-3 animate-spin" /> Reading the command log…
      </div>
    );
  }

  if (state.status === 'failed') {
    return <div className="mt-2 text-amber-700">Could not read the command log for this site.</div>;
  }

  if (state.commands.length === 0) {
    return <div className="mt-2 text-gray-500">Nothing has been sent to this valve yet.</div>;
  }

  return (
    <div className="mt-2">
      <RecentActivity
        results={[]}
        commands={state.commands}
        routers={routers}
        transport={null}
        title="Recent activity"
        limit={5}
        compact
      />
    </div>
  );
}
